// react and js
import PropTypes from "prop-types";
import { React, useState, useEffect, useContext } from "react";
import { useTranslation } from "react-i18next";

// mui
import { Grid, Stack, Box, Typography } from "@mui/material";

// components
import backendRequest from "../../components/BackendRequest";
import LoadingAnimation from "../../components/LoadingAnimation";
import MissingContent from "../../components/MissingContent";
import CardOutlet from "../../components/CardOutlet";

// hooks
import appContext from "../../hooks/appContext";
import { stateStorageInit } from "../../hooks/stateStorageInit";

// layouts

// pages

// theme
import { B3Small } from "../../theme/styled";

// utils
import { TranslatedMessage } from "../../utils/common";

// i18n
import { localizeExternalUrl } from "../../i18n/languages";

// widgets
import {
  DistributionApplicationSubscriptionItem,
  DistributionTickDataSubscriptionItem,
} from "./Subscriptions";

// ----------------------------------------------------------------------
// Code
// ----------------------------------------------------------------------
function WidgetMainContentProcessedCombined({ content }) {
  WidgetMainContentProcessedCombined.propTypes = {
    content: PropTypes.any.isRequired,
  };

  const { t, i18n } = useTranslation();

  const websiteUrl = localizeExternalUrl(
    process.env.REACT_APP_URL_FRONTEND_WEBSITE,
    i18n.language
  );

  let applicationSubscriptionsAvailable = false;
  let tickDataSubscriptionsAvailable = false;

  content.subscriptions.forEach((sub) => {
    if (sub.accounts.length !== 0) {
      applicationSubscriptionsAvailable = true;
    } else {
      tickDataSubscriptionsAvailable = true;
    }
  });

  return (
    <Grid item xs={12} md={12} lg={12}>
      <Stack spacing={5}>
        <Grid container spacing={3}>
          <Grid item xs={12} md={12} lg={12}>
            <Box sx={{ mt: 0, mb: 0 }}>
              <CardOutlet
                variant="description"
                backgroundProp="#F2F2F2"
                title={t("subscriptions.combined.title")}
                subheader={t("subscriptions.combined.subheader")}
              />
            </Box>
          </Grid>

          {content.subscriptions.map((sub) => {
            const isAppSubscription = sub.accounts && sub.accounts.length !== 0;

            if (isAppSubscription) {
              return (
                <DistributionApplicationSubscriptionItem
                  key={sub.product.product_tag}
                  content={sub}
                  distributionPlansApplication={
                    content.distribution_plans[sub.product.product_name]
                  }
                  KnownAccountServer={content.known_account_server}
                  referenceTradingCurrency={content.reference_trading_currency}
                />
              );
            }

            return (
              <DistributionTickDataSubscriptionItem
                key={sub.product.product_tag}
                content={sub}
                distributionPlansTickData={
                  content.distribution_mt5_tick_data_plans
                    .distribution_mt5_tick_data_plans
                }
                KnownAccountServer={content.known_account_server}
                categories={
                  content.distribution_mt5_tick_data_plans.availability_cats
                }
              />
            );
          })}

          {!applicationSubscriptionsAvailable && !tickDataSubscriptionsAvailable && (
            <Grid item xs={12} md={12} lg={12}>
              <Box
                display="block"
                align="center"
                sx={{
                  borderRadius: "10px",
                  py: 1,
                  boxShadow: "0px 0px 4px 0px rgba(255,255,255,0.5)",
                  transition: "box-shadow 0.3s ease-in-out",
                  backgroundColor: "white",
                  "&:hover": {
                    boxShadow: "0px 0px 8px 0px rgba(255,255,255,0.7)",
                  },
                }}
              >
                <Box sx={{ mx: 4, my: "14px" }}>
                  <Typography
                    variant="contentBig"
                    display="block"
                    align="center"
                    sx={{ mb: 2 }}
                  >
                    {t("subscriptions.combined.emptyState")}
                  </Typography>
                  <B3Small
                    fullWidth
                    variant="outlined"
                    href={websiteUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                  >
                    {t("subscriptions.combined.solutionsButton")}
                  </B3Small>
                </Box>
              </Box>
            </Grid>
          )}
        </Grid>
      </Stack>
    </Grid>
  );
}

// ----------------------------------------------------------------------
// Properties
// ----------------------------------------------------------------------

// ----------------------------------------------------------------------
// Main element export(s)
// ----------------------------------------------------------------------
export default function Widget() {
  Widget.propTypes = {};

  const { t } = useTranslation();
  const { stateStorage, setStateStorage } = useContext(appContext);
  const myLocalStateStorage = JSON.parse(
    localStorage.getItem("myLocalStateStorage")
  );

  const [error, setError] = useState(false);
  const [, setWarning] = useState(false);
  const [, setSuccess] = useState(false);

  const [loading, setLoading] = useState(navigator.onLine);
  const [content, setContent] = useState();
  const [, setReturnMessage] = useState("");

  const [regularRefreshIntervalCounter, setRegularRefreshIntervalCounter] =
    useState(0);

  // ######################################################
  // Main backend request
  // ######################################################
  function MainRequest(periodic) {
    if (
      localStorage.getItem("myReauthentication") === "false" &&
      navigator.onLine === true
    ) {
      if (periodic === false) {
        setStateStorage((previousState) => ({
          ...previousState,
          widgetUpdateSubscriptionsSubscriptions: false,
        }));

        setError(false);
        setWarning(false);
        setSuccess(false);
        setReturnMessage("");
      }

      const sendData = {
        request_type: "dashboard",
        route_info: "subscriptions",
        operation: "get_essentials",
        client: {
          dashboard_access_token:
            myLocalStateStorage.answer.dashboard_access_token,
        },
      };
      backendRequest(process.env.REACT_APP_URL_API_DASHBOARD, sendData)
        .then((contentFromBackend) => {
          const keyOnly = Object.keys(contentFromBackend);
          const checkKey = keyOnly.includes("message");

          if (checkKey === true) {
            if (contentFromBackend.message === "success") {
              setError(false);
              setLoading(false);
              setContent(contentFromBackend);
            } else if (contentFromBackend.message === "token invalid") {
              setWarning(true);
              setLoading(false);
              setReturnMessage(<TranslatedMessage tKey="subscriptions.messages.loggedOut" />);

              setStateStorage(stateStorageInit);
              localStorage.setItem("forcedLoggedOut", true);

              localStorage.setItem("myLocalStateStorage", JSON.stringify(""));
              localStorage.removeItem("myLocalStateStorage");

              sessionStorage.setItem("myLocalLastRoute", JSON.stringify(""));
              sessionStorage.removeItem("myLocalLastRoute");

              localStorage.setItem("myLocalSettingStorage", JSON.stringify(""));
              localStorage.removeItem("myLocalSettingStorage");

              localStorage.setItem("myReauthentication", JSON.stringify(""));
              localStorage.removeItem("myReauthentication");
            } else {
              setError(true);
              setLoading(false);
              setReturnMessage(<TranslatedMessage tKey="subscriptions.messages.invalidRequest" />);
            }
          } else {
            setError(true);
            setLoading(false);
            setReturnMessage(<TranslatedMessage tKey="subscriptions.messages.unknownError" />);
          }
        })
        .catch(() => {
          if (navigator.onLine === true) {
            setError(true);
            setLoading(false);
            setReturnMessage(<TranslatedMessage tKey="subscriptions.messages.unknownError" />);
          }
        });
    }
  }

  // ######################################################
  // Inital and triggered refreshs
  // ######################################################
  useEffect(() => {
    if (!stateStorage.widgetUpdateSubscriptionsSubscriptions) return;

    MainRequest(false);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [stateStorage.widgetUpdateSubscriptionsSubscriptions]);

  // ######################################################
  // Regular refreshs
  // ######################################################
  useEffect(() => {
    const timerID = setTimeout(() => {
      setRegularRefreshIntervalCounter(regularRefreshIntervalCounter + 1);
    }, stateStorage.widgetUpdateIntervalSubscriptionsSubscriptions);

    return () => {
      clearTimeout(timerID);
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regularRefreshIntervalCounter]);

  useEffect(() => {
    MainRequest(true);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [regularRefreshIntervalCounter]);

  // ######################################################
  // Data representation
  // ######################################################
  let WidgetOutlet = <MissingContent message={t("subscriptions.title")} />;
  if (loading) {
    WidgetOutlet = <LoadingAnimation />;
  } else if (error) {
    WidgetOutlet = <MissingContent message={t("subscriptions.title")} />;
  } else if (content) {
    WidgetOutlet = <WidgetMainContentProcessedCombined content={content} />;
  }

  // ######################################################
  // Outlet
  // ######################################################
  return WidgetOutlet;
}
